import { useEffect, useMemo, useState, useCallback } from 'react';
import { TrendingUp, UserPlus, AlertTriangle, DollarSign, ArrowUpRight, Eye, Zap, X as XIcon } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FilterBar, type ActiveFilter, type FilterField } from '@/components/ui/FilterBar';
import { StatsCard } from '@/components/ui/StatsCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { AiActionButton } from '@/components/ui/AiActionButton';
import { SignalTypeBadge } from '@/components/modules/SignalTypeBadge';
import { AutonomyIndicator } from '@/components/modules/AutonomyIndicator';
import { formatCurrency, formatDate } from '@/lib/format';
import { SignalSheet } from '@/components/sheets/SignalSheet';
import { useGrowthStore } from '@/stores/growthStore';
import { toast } from '@/stores/toastStore';
import { api } from '@/lib/api';
import type { ApiResponse, GrowthSignal } from '@nexus/shared/types';

const statusVariant: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  new: 'info',
  reviewed: 'warning',
  actioned: 'success',
  dismissed: 'default',
};

const GROWTH_FILTER_FIELDS: FilterField[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'new', label: 'New' },
      { value: 'reviewed', label: 'Reviewed' },
      { value: 'actioned', label: 'Actioned' },
      { value: 'dismissed', label: 'Dismissed' },
    ],
  },
  {
    key: 'signalType',
    label: 'Type',
    type: 'select',
    options: [
      { value: 'lead_score', label: 'Lead Score' },
      { value: 'churn_risk', label: 'Churn Risk' },
      { value: 'upsell', label: 'Upsell' },
      { value: 'competitive', label: 'Competitive' },
      { value: 'revenue_leak', label: 'Revenue Leak' },
      { value: 'engagement', label: 'Engagement' },
    ],
  },
  { key: 'score', label: 'Score', type: 'number_range' },
];

export function GrowthEngine() {
  const { signals, stats, isLoading, error, fetch, fetchStats, markReviewed, markActioned, dismiss, clearError } = useGrowthStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [aiScanning, setAiScanning] = useState(false);

  const handleAiScan = useCallback(async () => {
    setAiScanning(true);
    toast.info('Scanning...', 'AI is scanning for growth signals. This may take a moment.');
    try {
      const res = await api.post<ApiResponse<{ generated: number; signals: unknown[] }>>('/api/growth/ai-scan');
      const count = res.data?.generated ?? 0;
      toast.success(`Generated ${count} new signal${count !== 1 ? 's' : ''}`);
      fetch();
      fetchStats();
    } catch {
      toast.error('AI Scan failed', 'Check that an AI provider is configured in Settings.');
    } finally {
      setAiScanning(false);
    }
  }, [fetch, fetchStats]);

  useEffect(() => {
    fetch();
    fetchStats();
  }, [fetch, fetchStats]);

  const filteredSignals = useMemo(() => {
    if (filters.length === 0) return signals;
    return signals.filter((signal) => {
      return filters.every((f) => {
        if (!f.value) return true;
        const val = String((signal as unknown as Record<string, unknown>)[f.key] ?? '').toLowerCase();
        const filterVal = f.value.toLowerCase();
        switch (f.operator) {
          case 'eq': return val === filterVal;
          case 'neq': return val !== filterVal;
          case 'contains': return val.includes(filterVal);
          case 'gt': return Number(val) > Number(f.value);
          case 'lt': return Number(val) < Number(f.value);
          default: return true;
        }
      });
    });
  }, [signals, filters]);

  const pipelineData = useMemo(() => {
    const counts: Record<string, number> = { new: 0, reviewed: 0, actioned: 0, dismissed: 0 };
    for (const s of signals) counts[s.status] = (counts[s.status] || 0) + 1;
    return [
      { stage: 'New', count: counts.new },
      { stage: 'Reviewed', count: counts.reviewed },
      { stage: 'Actioned', count: counts.actioned },
      { stage: 'Dismissed', count: counts.dismissed },
    ];
  }, [signals]);

  const newLeads = stats?.new ?? signals.filter((s) => s.signalType === 'lead_score').length;
  const churnRisks = signals.filter((s) => s.signalType === 'churn_risk').length;
  const upsellOpps = signals.filter((s) => s.signalType === 'upsell').length;
  const revenueLeaks = signals.filter((s) => s.signalType === 'revenue_leak').length;

  const handleReview = async (id: string) => {
    try { await markReviewed(id); toast.success('Signal reviewed'); } catch { /* handled by store */ }
  };
  const handleAction = async (id: string) => {
    try { await markActioned(id); toast.success('Signal actioned'); } catch { /* handled by store */ }
  };
  const handleDismiss = async (id: string) => {
    try { await dismiss(id); toast.success('Signal dismissed'); } catch { /* handled by store */ }
  };

  const columns: Column<GrowthSignal>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <SignalTypeBadge type={row.signalType} />
          <AutonomyIndicator
            level={1}
            action="AI-detected signal"
            compact
          />
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (row) => <p className="text-sm text-surface-200 truncate max-w-xs">{row.title}</p>,
    },
    {
      key: 'score',
      header: 'Score',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-1.5 bg-surface-700 rounded-full overflow-hidden">
            <div className="h-full bg-nexus-500 rounded-full" style={{ width: `${row.score}%` }} />
          </div>
          <span className="text-xs font-medium text-surface-300">{row.score}</span>
        </div>
      ),
    },
    {
      key: 'impact',
      header: 'Impact',
      sortable: true,
      render: (row) => (
        <span className={`text-sm font-medium ${(row.projectedImpact ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {row.projectedImpact != null
            ? `${row.projectedImpact >= 0 ? '+' : ''}${formatCurrency(row.projectedImpact)}`
            : '--'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={statusVariant[row.status] || 'default'}>{row.status}</Badge>,
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400">{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.status === 'new' && (
            <>
              <Button variant="ghost" size="sm" icon={<Eye className="h-3.5 w-3.5" />} onClick={() => handleReview(row.id)}>
                Review
              </Button>
              <Button variant="ghost" size="sm" icon={<XIcon className="h-3.5 w-3.5" />} onClick={() => handleDismiss(row.id)}>
                Dismiss
              </Button>
            </>
          )}
          {row.status === 'reviewed' && (
            <>
              <Button variant="ghost" size="sm" icon={<Zap className="h-3.5 w-3.5" />} onClick={() => handleAction(row.id)}>
                Action
              </Button>
              <Button variant="ghost" size="sm" icon={<XIcon className="h-3.5 w-3.5" />} onClick={() => handleDismiss(row.id)}>
                Dismiss
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (isLoading && signals.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div>
          <Skeleton className="h-6 w-36 mb-2" />
          <Skeleton className="h-4 w-80" />
        </div>
        {/* Stats — 4 cards */}
        <SkeletonStats count={4} />
        {/* Main grid: table 2/3 + chart 1/3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonTable rows={6} cols={7} />
          </div>
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <Skeleton className="h-4 w-28 mb-4" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Growth Engine</h2>
          <p className="text-sm text-surface-400 mt-0.5">AI-powered growth signals, pipeline analytics, and revenue optimization</p>
        </div>
        <AiActionButton
          label="AI Scan for Signals"
          onClick={handleAiScan}
          loading={aiScanning}
        />
      </div>

      {error && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Signal cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="New Leads" value={newLeads} icon={<UserPlus className="h-5 w-5" />} />
        <StatsCard label="Churn Risks" value={churnRisks} icon={<AlertTriangle className="h-5 w-5" />} />
        <StatsCard label="Upsell Opportunities" value={upsellOpps} icon={<ArrowUpRight className="h-5 w-5" />} />
        <StatsCard label="Revenue Leaks" value={revenueLeaks} icon={<DollarSign className="h-5 w-5" />} />
      </div>

      {/* Filters */}
      <FilterBar
        fields={GROWTH_FILTER_FIELDS}
        filters={filters}
        onChange={setFilters}
        storageKey="nexus:growth-filters"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signals table */}
        <div className="lg:col-span-2">
          <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
            <DataTable columns={columns} data={filteredSignals} onRowClick={(row) => setSelectedId(row.id)} />
          </div>
        </div>

        {/* Lead pipeline funnel */}
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-surface-200 mb-4">Lead Pipeline</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  type="number"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#334155' }}
                />
                <YAxis
                  type="category"
                  dataKey="stage"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#334155' }}
                  width={90}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#f1f5f9',
                  }}
                />
                <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Signal Detail Sheet */}
      <SignalSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
